import styled from 'styled-components';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import classNames from 'classnames';
import { useSlider } from '../../SliderContext';
import { Count } from '../Count';
import { relativePositionAffixes } from '../constants';
import { Progress } from '../Progress';

interface LineProps {
  index: number;
  currentIndex: number;
  paginationClassName: string;
  autoplayProgressBar: boolean;
  autoplayEnabled: boolean;
  autoplayPauseOnHover: boolean;
  goTo: (index: number) => void;
  seconds: number;
  slidesLength: number;
  active: boolean;
  last: boolean;
  withShadow: boolean;
}

export const Line = ({
  index,
  currentIndex,
  paginationClassName,
  autoplayProgressBar,
  autoplayEnabled,
  autoplayPauseOnHover,
  goTo,
  seconds,
  slidesLength,
  active,
  last,
  withShadow
}: LineProps) => {
  const { appClassnamePrefix } = useSlider();

  const positionAffix = relativePositionAffixes[index - currentIndex + 2];
  const withProgress = autoplayProgressBar && autoplayEnabled && active;

  return (
    <LineWrapper
      role="button"
      tabIndex={active ? -1 : 0}
      aria-label={`Slide ${index + 1} of ${slidesLength}`}
      _appClassPrefix={appClassnamePrefix}
      _isActive={active}
      _paginationClass={paginationClassName}
      _positionAffix={positionAffix}
      _withProgress={withProgress}
      onClick={() => goTo(index)}
      onKeyDown={getHandleActionKeyDown(() => goTo(index))}
    >
      <StyledLine
        _appClassPrefix={appClassnamePrefix}
        _paginationClass={paginationClassName}
        _withShadow={withShadow}
      >
        {withProgress && (
          <Count
            active={active}
            seconds={seconds}
            autoplayProgressBar={autoplayProgressBar}
            autoplayPauseOnHover={autoplayPauseOnHover}
            last={last}
          >
            {({ percent }: { percent: number }) => (
              <Progress.Line
                percent={percent}
                height={2}
                appClassnamePrefix={appClassnamePrefix}
              />
            )}
          </Count>
        )}
      </StyledLine>
    </LineWrapper>
  );
};

interface StyledLineProps {
  _appClassPrefix: string;
  _paginationClass: string;
  _withShadow: boolean;
}

const StyledLine = styled.div.attrs<StyledLineProps>(
  ({ _appClassPrefix, _paginationClass }) => ({
    className: classNames(
      { [`${_paginationClass}-line`]: _paginationClass },
      `${_appClassPrefix}-line-line`
    )
  })
)`
  width: 44px;
  margin: 0 4px;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  transition: 0.3s all ease;
  backface-visibility: hidden;
  height: 2px;

  ${({ _withShadow }) =>
    _withShadow && 'box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);'}
`;

interface LineWrapperProps {
  _appClassPrefix: string;
  _isActive: boolean;
  _paginationClass: string;
  _positionAffix: string;
  _withProgress: boolean;
}

const LineWrapper = styled.div.attrs<LineWrapperProps>(
  ({
    _appClassPrefix,
    _isActive,
    _paginationClass,
    _positionAffix,
    _withProgress
  }) => ({
    className: classNames(`${_appClassPrefix}-line-item`, {
      [`${_appClassPrefix}-line-${_positionAffix}`]: _positionAffix,
      [`${_paginationClass}-item`]: _paginationClass,
      [`${_paginationClass}-item-${
        _isActive ? '' : 'active-'
      }${_positionAffix}`]: _paginationClass && _positionAffix,
      [`${_paginationClass}-with-progress`]: _withProgress
    })
  })
)`
  padding: 12px 0;
  cursor: pointer;
  position: relative;
  transition: 0.3s all ease;
  opacity: ${({ _isActive }) => (_isActive ? 1 : 0.5)};

  &:hover {
    opacity: 1;
  }
`;
